'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

/**
 * One IntersectionObserver for every `.reveal` on the page, mounted once in the
 * root layout. Replaces the per-element observers the Reveal component used to
 * create - same behaviour, a single client component instead of dozens.
 *
 * Re-scans on navigation, since App Router swaps the tree without remounting
 * the layout. If the API is missing, everything is simply shown at once, which
 * is also what the noscript rule in layout.tsx does.
 */
export function RevealObserver() {
  const pathname = usePathname();

  useEffect(() => {
    const show = (el: Element) => el.classList.add('is-visible');
    const targets = () => document.querySelectorAll('.reveal:not(.is-visible)');

    if (typeof IntersectionObserver === 'undefined') {
      targets().forEach(show);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            show(entry.target);
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' },
    );

    targets().forEach((el) => io.observe(el));

    // Sections can arrive after this runs (a Suspense boundary resolving, the
    // deferred chat demo mounting), so pick up anything added later.
    const mo = new MutationObserver(() => {
      targets().forEach((el) => io.observe(el));
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [pathname]);

  return null;
}
